// src/pages/income/env/disinfection/DisinfectionInvoicePage.jsx
// 환경/소독 청구서 관리 — 발행은 수입 건 저장 시 자동으로 이뤄지므로(income
// 앱 연동, disinfection_invoice/signals.py) 이 페이지는 발행 이력 조회/수동
// 보정(청구일자·용역명·회차)/재다운로드/삭제/거래처 관리 전용이다.
import React, { useCallback, useEffect, useState } from "react";
import api from "../../../../api";
import styles from "./DisinfectionInvoicePage.module.css";
import DisinfectionClientModal from "./DisinfectionClientModal";

function downloadBlobResponse(res, filename) {
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function blobErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error || err?.message || fallback;
}

export default function DisinfectionInvoicePage() {
  useEffect(() => {
    document.title = "환경/소독 청구서 관리";
  }, []);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [clientFilter, setClientFilter] = useState("");

  const [clients, setClients] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [mergeDownloading, setMergeDownloading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ issue_date: "", service_name: "", round_no: "" });

  const [showClientModal, setShowClientModal] = useState(false);

  const loadClients = useCallback(async () => {
    const res = await api.get("disinfection/clients/");
    setClients(res.data);
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { year, month };
      if (clientFilter) params.client = clientFilter;
      const res = await api.get("disinfection/invoices/", { params });
      setHistory(res.data);
      setCheckedIds({});
    } finally {
      setHistoryLoading(false);
    }
  }, [year, month, clientFilter]);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const toggleCheck = (id, checked) => {
    setCheckedIds((prev) => ({ ...prev, [id]: checked }));
  };

  const toggleAll = (checked) => {
    const next = {};
    history.forEach((inv) => { next[inv.id] = checked; });
    setCheckedIds(next);
  };

  const checkedList = history.filter((inv) => checkedIds[inv.id]);

  const startEdit = (inv) => {
    setEditingId(inv.id);
    setEditForm({ issue_date: inv.issue_date, service_name: inv.service_name, round_no: inv.round_no });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (inv) => {
    setSavingId(inv.id);
    try {
      await api.patch(`disinfection/invoices/${inv.id}/`, {
        issue_date: editForm.issue_date,
        service_name: editForm.service_name,
        round_no: Number(editForm.round_no),
      });
      await api.post(`disinfection/invoices/${inv.id}/regenerate/`);
      setEditingId(null);
      await loadHistory();
    } catch (e) {
      alert("저장 실패: " + (e?.response?.data?.error || e.message));
    } finally {
      setSavingId(null);
    }
  };

  const redownload = async (invoice) => {
    const res = await api.get(`disinfection/invoices/${invoice.id}/download/`, { responseType: "blob" });
    downloadBlobResponse(
      res,
      `${invoice.client_name}_${invoice.year}-${String(invoice.month).padStart(2, "0")}_${invoice.round_no}회차.pdf`
    );
  };

  const deleteInvoice = async (invoice) => {
    const ok = window.confirm(
      `[${invoice.client_name}] ${invoice.year}년 ${invoice.month}월 제${invoice.round_no}회차 청구서를 삭제하시겠습니까?\n` +
      `삭제 후 해당 거래처의 다음 발행 시 회차가 재계산되니 주의하세요.`
    );
    if (!ok) return;
    await api.delete(`disinfection/invoices/${invoice.id}/`);
    await loadHistory();
  };

  const mergeDownload = async () => {
    if (checkedList.length === 0) {
      alert("병합 다운로드할 청구서를 선택하세요.");
      return;
    }
    setMergeDownloading(true);
    try {
      const res = await api.post(
        "disinfection/invoices/merge-existing/",
        { ids: checkedList.map((inv) => inv.id) },
        { responseType: "blob" }
      );
      downloadBlobResponse(
        res,
        `disinfection_invoices_${year}-${String(month).padStart(2, "0")}_merged.pdf`
      );
    } catch (e) {
      alert("병합 다운로드 실패: " + (await blobErrorMessage(e, e.message)));
    } finally {
      setMergeDownloading(false);
    }
  };

  const checkedCount = checkedList.length;

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>환경/소독 청구서 관리</h2>
        <button className={styles.manageBtn} onClick={() => setShowClientModal(true)}>
          거래처 관리
        </button>
      </div>

      <div className={styles.periodRow}>
        <span className={styles.label}>년도</span>
        <select className={styles.selectInput} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i).map((y) => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
        <span className={styles.label}>월</span>
        <select className={styles.selectInput} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{m}월</option>
          ))}
        </select>
        <span className={styles.label}>거래처</span>
        <select className={styles.selectInput} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
          <option value="">전체</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.partner_name}</option>
          ))}
        </select>
        <button className={styles.bulkBtn} onClick={mergeDownload} disabled={mergeDownloading || checkedCount === 0}>
          {mergeDownloading ? "다운로드 중..." : `선택 병합 PDF 다운로드 (${checkedCount})`}
        </button>
      </div>

      <div className={styles.historySection}>
        <h3 className={styles.historyTitle}>발행 이력 ({year}년 {month}월)</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  checked={history.length > 0 && checkedCount === history.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </th>
              <th>시설명</th>
              <th>회차</th>
              <th>용역명</th>
              <th>금액</th>
              <th>청구일자</th>
              <th className={styles.actionCell}>수정</th>
              <th className={styles.actionCell}>다운로드</th>
              <th className={styles.actionCell}>삭제</th>
            </tr>
          </thead>
          <tbody>
            {historyLoading ? (
              <tr><td colSpan={9} className={styles.emptyCell}>불러오는 중...</td></tr>
            ) : history.length === 0 ? (
              <tr><td colSpan={9} className={styles.emptyCell}>발행 이력이 없습니다.</td></tr>
            ) : (
              history.map((inv) => {
                const isEditing = editingId === inv.id;
                return (
                  <tr key={inv.id}>
                    <td className={styles.checkboxCell}>
                      <input
                        type="checkbox"
                        checked={!!checkedIds[inv.id]}
                        onChange={(e) => toggleCheck(inv.id, e.target.checked)}
                      />
                    </td>
                    <td className={styles.nowrapCell}>{inv.client_name}</td>
                    <td>
                      {isEditing ? (
                        <input
                          className={`${styles.editableInput} ${styles.roundInput}`}
                          type="number"
                          value={editForm.round_no}
                          onChange={(e) => setEditForm((p) => ({ ...p, round_no: e.target.value }))}
                        />
                      ) : `${inv.round_no}회차`}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          className={styles.editableInput}
                          type="text"
                          value={editForm.service_name}
                          onChange={(e) => setEditForm((p) => ({ ...p, service_name: e.target.value }))}
                        />
                      ) : inv.service_name}
                    </td>
                    <td className={styles.nowrapCell}>{Number(inv.amount).toLocaleString()}원</td>
                    <td>
                      {isEditing ? (
                        <input
                          className={styles.editableInput}
                          type="date"
                          value={editForm.issue_date}
                          onChange={(e) => setEditForm((p) => ({ ...p, issue_date: e.target.value }))}
                        />
                      ) : inv.issue_date}
                    </td>
                    <td className={styles.actionCell}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: "4px" }}>
                          <button className={styles.saveBtn} onClick={() => saveEdit(inv)} disabled={savingId === inv.id}>
                            {savingId === inv.id ? "저장중" : "저장"}
                          </button>
                          <button className={styles.linkBtn} onClick={cancelEdit}>취소</button>
                        </div>
                      ) : (
                        <button className={styles.linkBtn} onClick={() => startEdit(inv)}>수정</button>
                      )}
                    </td>
                    <td className={styles.actionCell}>
                      <button className={styles.linkBtn} onClick={() => redownload(inv)}>다운로드</button>
                    </td>
                    <td className={styles.actionCell}>
                      <button className={styles.linkBtnDanger} onClick={() => deleteInvoice(inv)}>삭제</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showClientModal && (
        <DisinfectionClientModal
          onClose={() => setShowClientModal(false)}
          onChanged={loadClients}
        />
      )}
    </div>
  );
}
